import React, { useEffect, useMemo, useState } from "react";
import styles from "./EngagementTable.module.scss";
import { useAppSelector } from "../../../../../redux/hooks";
import { formatDate } from "../../../../../utils/dateFormatter";
import {
  channelOptions,
  countryCodeMappings,
  typeOptions,
} from "../../../../../constants/dataConstants";
import { useTranslation } from "react-i18next";
import Icon from "../../../../common/Icon/Icon";
import Tooltip from "../../../../common/Tooltip/Tooltip";
import Modal from "../../../../common/Modal/Modal";
import DeleteConfirmationPopUp from "../../../../common/DeleteConfirmationPopUp/DeleteConfirmationPopUp";
import AddEngagementPopup from "../AddEngagementPopup/AddEngagementPopup";

// sort icons
import arrow_up_light from "../../../../../assets/images/LightTheme/arrow_up_light.svg";
import arrow_up_dark from "../../../../../assets/images/DarkTheme/arrow_up_dark.svg";
import arrow_down_light from "../../../../../assets/images/LightTheme/arrow_down_light.svg";
import arrow_down_dark from "../../../../../assets/images/DarkTheme/arrow_down_dark.svg";
import nosort_light from "../../../../../assets/images/LightTheme/nosort_light.svg";
import nosort_dark from "../../../../../assets/images/DarkTheme/nosort_dark.svg";
import TextButton from "../../../../common/TextButton/TextButton";
import { Engagement } from "../Engagements";
import HTMLtoText from "../../../../common/HTMLToText/HTMLToText";
import {
  useLazyDownloadEngagementQuery
} from "../../../../../redux/services/goalsAPI";
import { useProfileParams } from "../../../../../utils/common";
import Spinner from "../../../../common/Spinner/Spinner";

export type SortKey = "title" | "date" | "type" | "description";
export type SortOrder = "asc" | "desc" | "nosort";

interface ColumnState {
  column: SortKey;
  sortOrder: SortOrder;
}

interface Props {
  data: Engagement[];
  onDelete?: (id: string) => void;
  onEdit?: (item: Engagement) => void;
  rowLimit?: number;
  isHistoryGoalData?: boolean;
}

type SortableColumn = {
  label: string;
  sorting: true;
  sortingKey: SortKey;
};

type NonSortableColumn = {
  label: string;
  sorting: false;
  sortingKey?: never;
};

type TableHeaderColumn = SortableColumn | NonSortableColumn;

const TableHeaderConfig: { [key: string]: TableHeaderColumn } = {
  title: {
    label: "engagementInfo",
    sorting: false, // Remove the sorting on this section for this release
    // sortingKey: "title",
  },
  type: {
    label: "type",
    sorting: true,
    sortingKey: "type",
  },
  description: {
    label: "description",
    sorting: true,
    sortingKey: "description",
  },
  actions: {
    label: "actions",
    sorting: false,
  },
};

const EngagementTable: React.FC<Props> = ({
  data,
  onDelete,
  onEdit,
  rowLimit = 3,
  isHistoryGoalData
}) => {
  const { t } = useTranslation();
  const [columnState, setColumnState] = useState<ColumnState>({
    column: "date",
    sortOrder: "desc",
  });
  const [isViewMore, setViewMore] = useState(false);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const userDetails = useAppSelector((state: any) => state.userDetails?.user);
  const userAffiliateCode = userDetails?.userAffiliateCode || "US";

  const { affiliateCode, aboNumber } = useProfileParams();
  // Local modal state
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Engagement | null>(null);

  const sortedData = useMemo(() => {
    const list = [...data];
    const { column, sortOrder } = columnState;

    if (sortOrder === "nosort") return list;

    list.sort((a, b) => {
      let av = (a as any)[column];
      let bv = (b as any)[column];

      if (column === "date") {
        av = new Date(av).getTime();
        bv = new Date(bv).getTime();
      } else {
        av = (av || "").toString().toLowerCase();
        bv = (bv || "").toString().toLowerCase();
      }

      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, columnState]);

  useEffect(() => {
    setViewMore(false);
  }, [data]);

  const toggleSort = (col: SortKey) => {
    setColumnState((prev) => {
      if (prev.column !== col) return { column: col, sortOrder: "asc" };
      const next =
        prev.sortOrder === "asc"
          ? "desc"
          : prev.sortOrder === "desc"
            ? "nosort"
            : "asc";
      return { column: col, sortOrder: next };
    });
  };
  const getTypeLabel = (code: string | number) =>
    typeOptions.find((t) => String(t.code) === String(code))?.value || code;

  const getChannelLabel = (code: string | number | undefined) =>
    channelOptions.find((c) => String(c.code) === String(code))?.value || code;

  const sortIcon = (col: SortKey) => {
    const active =
      columnState.column === col ? columnState.sortOrder : "nosort";
    if (active === "asc")
      return currentTheme === "light" ? arrow_up_light : arrow_up_dark;
    if (active === "desc")
      return currentTheme === "light" ? arrow_down_light : arrow_down_dark;
    return currentTheme === "light" ? nosort_light : nosort_dark;
  };

  const [
    getDownloadUrl,
    { isLoading: getDownloadUrlLoading, isError: getDownloadUrlError, isFetching: getDownloadUrlFetching },
  ] = useLazyDownloadEngagementQuery();

  const handleConfirmDelete = () => {
    if (deleteTargetId && onDelete) onDelete(deleteTargetId);
    setDeleteTargetId(null);
  };

  const openEdit = (item: Engagement) => setEditTarget(item);

  const closeEdit = () => setEditTarget(null);

  const saveEdit = (updated: any) => {
    if (!editTarget) return;

    const mergedEngagement: Engagement = {
      ...editTarget,
      title: updated.title,
      date: updated.date || editTarget.date,
      type: updated?.type ?? updated?.type?.code ?? editTarget.type,
      description: updated.description ?? editTarget.description,
      channel: updated?.channel ?? updated?.channel?.code ?? editTarget.channel,
      attachments: (updated.attachments || []).map(
        (file: any, idx: number) => ({
          id: file.aboGoalEngagementDocumentId,
          name: file.name,
          url: file.dataUrl || "#",
          type: file.type,
          fileObject: file.fileObject,
          isExisting: !!file.aboGoalEngagementDocumentId || file.isExisting,
        })
      ),
    };

    const payloadForUpdate = { ...(mergedEngagement as any), aboGoalIds: updated?.aboGoalIds };

    onEdit && onEdit(payloadForUpdate);
    setEditTarget(null);
  };

  const visibleRows = rowLimit
    ? isViewMore
      ? Math.min(sortedData.length, 15)
      : rowLimit
    : sortedData.length;

  const renderAttachmentsTooltip = (item: Engagement) => {
    const attachments = item.attachments || []; // fallback
    if (attachments.length === 0) {
      return (
        <span className={styles.attachments_title}>{t("noAttachments")}</span>
      );
    }
    return (
      getDownloadUrlLoading || getDownloadUrlFetching ? (
        <span className={styles.spinner}>
          <Spinner loadingSize={`medium`} />
        </span>
      ) :
        <div>
          <span className={styles.attachments_title}>{t("attachments")}:</span>
          {attachments.map((file) => (
            <div key={file.id} className={styles.file}>
              <span
                className={styles.file_link}
                onClick={(e) => downloadEngagementDocument(item?.title, file.id, item.id)}
              >
                {file.name}
              </span>
              <span className={styles.file_type}>{file.type}</span>
            </div>
          ))}
        </div>)
  };
  const downloadEngagementDocument = async (
    title: string,
    aboGoalEngagementDocumentId: string | number,
    aboGoalEngagementId: string | number
  ) => {
    getDownloadUrl({
      affiliateCode,
      aboNumber,
      aboGoalEngagementDocumentId,
      aboGoalEngagementId
    }).then((response) => {

      const url = response?.data?.body?.path;
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute("download", `${title}`);
      document.body.appendChild(tempLink);
      tempLink.click();

      // Clean up the temporary elements and URL
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    }).catch(error => {
      console.error("Error downloading Docs:", error);
    })
  };

  return (
    <section className={`${styles.table_container}`}>
      <div className={`${styles.table_wrapper}`}>
        <table className={styles.table}>
          <thead>
            {/* Header row */}
            <tr className={`${styles.table_header}`}>
              {Object.keys(TableHeaderConfig).map((key) => {
                const header = TableHeaderConfig[key];

                return (
                  <th
                    key={header.label}
                    className={`${styles.header_cell}`}
                    onClick={() =>
                      header.sorting ? toggleSort(header.sortingKey) : undefined
                    }
                  >
                    <div className={styles.header_cell_content}>
                      <span>{t(header.label)}</span>
                      {header.sorting && (
                        <img src={sortIcon(header.sortingKey)} alt="sort" />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.slice(0, visibleRows).map((item) => (
              <tr key={item.id} className={`${styles.table_data_row}`}>
                <td className={`${styles.data_cell}`}>
                  <div className={styles.info_cell}>
                    <span className={styles.engagement_name}>{item.title}</span>
                    <span className={styles.engagement_meta}>
                      {getChannelLabel(item.channel)} |{" "}
                      {formatDate(
                        item.date,
                        countryCodeMappings[userAffiliateCode] || "US",
                        "long"
                      )}
                    </span>
                  </div>
                </td>
                <td className={`${styles.data_cell}`}>
                  <span className={styles.type_name}>
                    {getTypeLabel(item.type)}
                  </span>
                </td>
                <td className={`${styles.data_cell}`}>
                  <HTMLtoText htmlContent={item.description} />
                </td>
                <td className={`${styles.data_cell}`}>
                  {!isHistoryGoalData && (
                      <div className={styles.action_btns_cell}>
                      {/* Attachments with badge + tooltip */}
                      <span className={styles.action_btn}>
                        <Tooltip
                          content={renderAttachmentsTooltip(item)}
                          width="240px"
                          bottom="33px"
                          tipPos
                          className={styles.tooltip}
                        >
                          <Icon
                            name="attachment"
                            color={
                              currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"
                            }
                          />
                          {item.attachments?.length > 0 && (
                            <span className={styles.badge}>
                              {item.attachments.length}
                            </span>
                          )}
                        </Tooltip>
                      </span>
  
                      {/* Delete */}
                      <span
                        className={styles.action_btn}
                        onClick={() => setDeleteTargetId(item.id)}
                      >
                        <Icon
                          name="trash"
                          size={16}
                          color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                        />
                      </span>
  
                      {/* Edit */}
                      <span
                        className={styles.action_btn}
                        onClick={() => openEdit(item)}
                      >
                        <Icon
                          name="edit_icon"
                          size={17}
                          color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                        />
                      </span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {rowLimit && data.length > rowLimit && (
          <div className={styles.viewmore_Section}>
            <TextButton
              label={isViewMore ? t("view_less") : t("view_more")}
              onClick={() => setViewMore(!isViewMore)}
            ></TextButton>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      <Modal
        showDrawer={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
      >
        <DeleteConfirmationPopUp
          onCancel={() => setDeleteTargetId(null)}
          onDelete={handleConfirmDelete}
          titleLabel={t("deleteEngagementHeaderText")}
          msgText={t("deleteEngagementMsg")}
          buttonLabelOne={t("no_cancel")}
          buttonLabelTwo={t("yes_delete")}
        />
      </Modal>

      {/* Edit engagement modal */}
      <Modal showDrawer={!!editTarget} onClose={closeEdit}>
        {editTarget && (
          <AddEngagementPopup
            initialData={{
              title: editTarget.title,
              type:
                typeOptions.find(
                  (t) => String(t.code) === String(editTarget.type)
                ) || undefined,
              channel:
                channelOptions.find(
                  (c) => String(c.code) === String(editTarget.channel)
                ) || undefined,
              date: editTarget.date,
              description: editTarget.description,
              attachments: editTarget.attachments,
              linkedGoals: editTarget.linkedGoals,
            }}
            onSave={saveEdit}
            onCancel={closeEdit}
          />
        )}
      </Modal>
    </section>
  );
};

export default EngagementTable;
